var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { observable, action, makeObservable } from "mobx";
import { DataCondition } from "../../utils";
import { throttle } from "throttle-debounce";
import { disposer, } from "../../../contracts";
import { composeKey } from "../helpers";
import { eventDeepMerge, marketDeepMerge, selectionDeepMerge } from "./deepMerge";
import { isErrorType, isAbortError } from "@draftkings/sportsbook-common-utils";
import { PLAYER_PAGE, PLAYER_PAGE_ERRORS, PLAYER_PAGE_WIDGET, TRACKED_UPDATE_ENTITIES } from "../../../businessLayer/helpers";
export class MarketboardRetriever {
    constructor(props) {
        this.subscribe = (subscriptionPartial) => {
            const event = new this.SubscribeToSportsbookPlayerEvents(subscriptionPartial, PLAYER_PAGE, this.processData, this.onErrorCallback);
            this.trackSocketEvent("SUBSCRIBE");
            return () => {
                event.deactivate();
                this.trackSocketEvent("UNSUBSCRIBE");
            };
        };
        this.processData = (response) => {
            this.add(response.data.add);
            this.change(response.data.change);
            this.remove(response.data.remove);
        };
        this.add = (payload) => {
            payload.events.forEach(e => {
                if (e) {
                    this.events.set(e.id, Object.assign(Object.assign({}, e), { scorecard: e.eventScorecard }));
                }
            });
            payload.markets.forEach(m => {
                const key = composeKey([m.eventId, m.subcategoryId]);
                const marketsPerIds = this.markets.get(key);
                if (marketsPerIds) {
                    marketsPerIds.set(m.id, m);
                }
                else {
                    this.markets.set(key, new Map([[m.id, m]]));
                    this.uniqueIdsByMarketIds.set(m.id, key);
                }
            });
            payload.selections.forEach(this.addSelections);
            this.trackSocketEvent(TRACKED_UPDATE_ENTITIES.ADD);
        };
        this.addSelections = (s) => {
            const selectionsPerId = this.selections.get(s.marketId);
            selectionsPerId ?
                selectionsPerId.set(s.id, s) :
                this.selections.set(s.marketId, new Map([[s.id, s]]));
            this.uniqueIdsBySelectionIds.set(s.id, s.marketId);
        };
        this.change = (payload) => {
            payload.events.forEach(e => {
                const existEvent = this.events.get(e.id);
                if (existEvent) {
                    eventDeepMerge(existEvent, e);
                }
            });
            payload.markets.forEach(m => {
                var _a;
                const key = this.uniqueIdsByMarketIds.get(m.id) || "";
                const oldMarket = (_a = this.markets.get(key)) === null || _a === void 0 ? void 0 : _a.get(m.id);
                if (oldMarket) {
                    marketDeepMerge(oldMarket, m);
                }
            });
            const selectionsUpdate = new Map();
            payload.selections.forEach(sel => {
                selectionsUpdate.set(sel.id, sel);
            });
            this.updateSelectionsStorage(selectionsUpdate);
            if (selectionsUpdate.size) {
                this.applyChangesSelections();
            }
            this.trackSocketEvent(TRACKED_UPDATE_ENTITIES.CHANGE);
        };
        this.remove = (payload) => {
            payload.events.forEach(id => this.events.delete(id));
            payload.markets.forEach(id => {
                const uniqueId = this.uniqueIdsByMarketIds.get(id);
                if (uniqueId) {
                    const markets = this.markets.get(uniqueId);
                    markets.delete(id);
                    if (markets.size === 0) {
                        this.uniqueIdsByMarketIds.delete(id);
                    }
                }
            });
            payload.selections.forEach(id => {
                var _a;
                const uniqueId = this.uniqueIdsBySelectionIds.get(id);
                if (uniqueId) {
                    (_a = this.selections.get(uniqueId)) === null || _a === void 0 ? void 0 : _a.delete(id);
                    this.uniqueIdsBySelectionIds.delete(id);
                }
            });
            this.trackSocketEvent(TRACKED_UPDATE_ENTITIES.REMOVE);
        };
        this.onErrorCallback = (error) => {
            this.logError({
                statusCode: "WebSocketConnectionError",
                description: error.message,
                playerId: this.playerId,
            });
            this.condition.set("Marketboard", DataCondition.ERROR);
        };
        this.onError = (err) => {
            const is404 = isErrorType(err) && err.errorStatus.code === PLAYER_PAGE_ERRORS.PLAYER_NOT_FOUND;
            if (is404) {
                this.condition.set("Marketboard", DataCondition.EMPTY);
            }
            else if (!isAbortError(err)) {
                this.condition.set("Marketboard", DataCondition.ERROR);
            }
            !is404 && this.trackEvent(PLAYER_PAGE_ERRORS.PLAYER_PAGE_ERROR, {
                statusCode: "GetPlayerDataError",
                description: err instanceof Error ? err.message : JSON.stringify(err),
                playerId: this.playerId,
            });
        };
        this.playerMarketsRequestResolver = props.playerMarketsRequestResolver;
        this.applyChangesSelections = props.throttleTimeout
            ? throttle(props.throttleTimeout, this.applyChangesSelectionsCore)
            : this.applyChangesSelectionsCore;
        this.sports = new Map();
        this.leagues = new Map();
        this.events = new Map();
        this.markets = new Map();
        this.sections = new Map();
        this.selections = new Map();
        this.subscriptionPartial = new Map();
        this.uniqueIdsByMarketIds = new Map();
        this.uniqueIdsBySelectionIds = new Map();
        this.selectionsStorage = new Map();
        this.condition = props.condition;
        this.isEventPushTelemetryEnabled = props.productConfig.isEventPushTelemetryEnabled;
        this.SubscribeToSportsbookPlayerEvents = props.SubscribeToSportsbookPlayerEvents;
        this.subscriptionDisposer = disposer;
        this.playerId = "";
        this.logError = props.logError;
        this.trackEvent = props.trackEvent;
        makeObservable(this, {
            playerId: observable,
            sports: observable,
            leagues: observable,
            events: observable,
            markets: observable,
            sections: observable,
            selections: observable,
            subscriptionPartial: observable,
            addInitialData: action,
            processData: action,
            add: action.bound,
            change: action.bound,
            remove: action.bound,
            onError: action,
            updateSelections: action.bound,
            clearData: action,
            addSelections: action,
            setPlayerId: action,
        });
    }
    activate() { }
    deactivate() {
        this.clearData();
        this.playerMarketsRequestResolver.cancel();
    }
    loadData(playerId) {
        this.condition.set("Marketboard", DataCondition.LOADING);
        this.subscriptionDisposer();
        this.getData(playerId).then(() => {
            const subscriptionPartial = this.subscriptionPartial.get(`playerMarkets-${playerId}`);
            if (subscriptionPartial) {
                this.subscriptionDisposer = this.subscribe(subscriptionPartial);
            }
        }).catch((error) => {
            this.onError(error);
        });
    }
    setParams(params) {
        const paramsPlayerId = params.playerId.toString();
        if (paramsPlayerId !== this.playerId) {
            this.setPlayerId(paramsPlayerId);
            this.clearData();
            this.loadData(paramsPlayerId);
        }
    }
    getData(playerId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.playerMarketsRequestResolver.cancel();
            if (playerId) {
                this.setPlayerId(playerId);
                const data = yield this.playerMarketsRequestResolver.send({
                    playerId: this.playerId,
                });
                if (data) {
                    this.addInitialData(data);
                    if (this.sections.size) {
                        this.condition.set("Marketboard", DataCondition.LOADED);
                    }
                    else {
                        this.condition.set("Marketboard", DataCondition.EMPTY);
                    }
                }
                else {
                    this.condition.set("Marketboard", DataCondition.ERROR);
                    throw new Error(`Player events not found for player id: ${playerId}`);
                }
            }
        });
    }
    addInitialData(data) {
        data.sports.forEach(s => this.sports.set(s.id, s));
        data.leagues.forEach(l => this.leagues.set(l.id, l));
        data.events.forEach(e => this.events.set(e.id, e));
        data.markets.forEach(m => {
            const key = composeKey([m.eventId, m.subcategoryId]);
            const marketsPerIDs = this.markets.get(key);
            marketsPerIDs ?
                marketsPerIDs.set(m.id, m) :
                this.markets.set(composeKey([m.eventId, m.subcategoryId]), new Map([[m.id, m]]));
            this.uniqueIdsByMarketIds.set(m.id, key);
        });
        data.selections.forEach(this.addSelections);
        data.sections.forEach(sec => this.sections.set(sec.name, sec));
        Object.keys(data.subscriptionPartials).forEach(key => this.subscriptionPartial.set(key, data.subscriptionPartials[key]));
    }
    setPlayerId(playerId) {
        this.playerId = playerId;
    }
    trackSocketEvent(clientAction) {
        if (!this.isEventPushTelemetryEnabled) {
            return;
        }
        this.trackEvent("LONGSHOT_EVENT", {
            clientAction,
            widget: PLAYER_PAGE_WIDGET,
        });
    }
    updateSelectionsStorage(update) {
        update.forEach(u => this.selectionsStorage.set(u.id, u));
    }
    applyChangesSelectionsCore() {
        this.updateSelections();
        this.selectionsStorage = new Map();
    }
    updateSelections() {
        this.selectionsStorage.forEach(newSelection => {
            const uniqueIds = this.uniqueIdsBySelectionIds.get(newSelection.id);
            const oldSelection = uniqueIds && this.selections.get(uniqueIds).get(newSelection.id);
            if (!oldSelection) {
                return;
            }
            selectionDeepMerge(oldSelection, newSelection);
        });
    }
    clearData() {
        this.subscriptionDisposer();
        this.sports.clear();
        this.leagues.clear();
        this.events.clear();
        this.markets.clear();
        this.sections.clear();
        this.selections.clear();
        this.subscriptionPartial.clear();
        this.uniqueIdsByMarketIds.clear();
        this.uniqueIdsBySelectionIds.clear();
    }
}
//# sourceMappingURL=MarketboardRetriever.js.map